import { LabelsAdapter, LabelsAdapterValue } from "common/labelsAdapter";
import { api, LightningElement, wire } from "lwc";
import { Variant as ButtonVariant } from "tds/button";
import { Config as LoadingStateConfig } from "tds/contentCollectionLoadingState";
import { ContentSummary, ContentSummaryConfig } from "tds/contentSummary";
import * as linkPreviewLocales from "./locales";
import * as loadingStateLocales from "../contentCollectionLoadingState/locales";
import { assignedSlotNames } from "common/slot";
import classNames from "classnames";

export type LinkPreviewAction = {
  id: string;
  label: string;
  variant: ButtonVariant;
  link?: {
    href: string;
    target?: string;
    rel?: string;
  };
};

// title is required for the dialog aria-label
export type LinkPreviewSummary = ContentSummary & { title: string };

export type LinkPreviewConfig = Pick<ContentSummaryConfig, "imageVariant">;

export default class LinkPreview extends LightningElement {
  @api summary!: LinkPreviewSummary;
  @api config!: LinkPreviewConfig;
  @api actions: LinkPreviewAction[] = [];
  @api loading = false;
  @api error?: string;

  @wire(LabelsAdapter, {
    locales: linkPreviewLocales,
    options: "$linkPreviewLabelOptions",
  })
  linkPreviewLabels: LabelsAdapterValue = {};

  @wire(LabelsAdapter, {
    locales: loadingStateLocales,
  })
  loadingStateLabels: LabelsAdapterValue = {};

  get linkPreviewLabelOptions() {
    const info = { title: this.summary.title };
    return { loading: info, preview: info };
  }

  get loadingStateConfig(): LoadingStateConfig | undefined {
    if (this.loading) {
      return { state: "loading", loading: { stencilCount: 1 } };
    }

    if (this.error) {
      return {
        state: "error",
        error: {
          title: this.loadingStateLabels["error.title"],
          refresh: false,
          description: this.error,
        },
      };
    }

    return undefined;
  }

  get dialogAriaLabel() {
    return this.loading ? this.linkPreviewLabels.loading : this.summary.title;
  }

  get summaryConfig(): ContentSummaryConfig {
    return { imageSize: "large", ...this.config };
  }

  get footerClass() {
    return classNames({
      "link-preview__actions":
        this.slots.includes("actions") || this.actions.length > 0,
    });
  }

  private slots: string = "";

  handleClickAction(event: Event) {
    const target = event.target as HTMLElement;
    this.dispatchEvent(new CustomEvent(target.dataset.id as string));
  }

  handleOpen() {
    this.dispatchEvent(new CustomEvent("open"));
  }

  handleClose() {
    this.dispatchEvent(new CustomEvent("close"));
  }

  handleSlotChange() {
    this.slots = assignedSlotNames(this.template).join(" ");
  }
}
